/**
 * Warning-period resolution, mirrored from the database (T-009).
 *
 * The settings UI previews which rule wins before the user saves. That preview
 * is only useful if it agrees with resolve_warning_days() in
 * 20260805000000_create_unit_documents_schema.sql, whose order is:
 *
 *   ORDER BY (company_id IS NOT NULL) DESC, (document_type_id IS NOT NULL) DESC
 *
 * so company beats type, type beats the global default, and an absent rule
 * falls back to DEFAULT_WARNING_DAYS.
 */

export const DEFAULT_WARNING_DAYS = 30;

export interface WarningPeriodRule {
  id?: string;
  companyId: string | null;
  documentTypeId: string | null;
  warningDays: number;
}

export type RuleScope = 'company+type' | 'company' | 'type' | 'global' | 'default';

export interface Resolution {
  warningDays: number;
  scope: RuleScope;
  rule: WarningPeriodRule | null;
}

/** Specificity, highest first — mirrors the SQL ORDER BY. */
function rank(rule: WarningPeriodRule): number {
  return (rule.companyId !== null ? 2 : 0) + (rule.documentTypeId !== null ? 1 : 0);
}

function scopeOf(rule: WarningPeriodRule): RuleScope {
  if (rule.companyId !== null && rule.documentTypeId !== null) return 'company+type';
  if (rule.companyId !== null) return 'company';
  if (rule.documentTypeId !== null) return 'type';
  return 'global';
}

/**
 * Which rule applies to a (company, type) pair, and why.
 *
 * A rule matches when each of its fields either equals the query or is null —
 * null meaning "any", exactly as the SQL predicate reads.
 */
export function resolveWarningDays(
  rules: WarningPeriodRule[],
  companyId: string | null,
  documentTypeId: string | null,
): Resolution {
  const matching = rules.filter(
    (r) =>
      (r.companyId === companyId || r.companyId === null) &&
      (r.documentTypeId === documentTypeId || r.documentTypeId === null),
  );

  if (matching.length === 0) {
    return { warningDays: DEFAULT_WARNING_DAYS, scope: 'default', rule: null };
  }

  const winner = matching.reduce((best, r) => (rank(r) > rank(best) ? r : best));
  return { warningDays: winner.warningDays, scope: scopeOf(winner), rule: winner };
}

/** Human explanation of the winning rule, for the settings UI. */
export function explainResolution(res: Resolution): string {
  switch (res.scope) {
    case 'company+type': return 'this company, this document type';
    case 'company': return 'this company, all document types';
    case 'type': return 'all companies, this document type';
    case 'global': return 'the global override';
    default: return `the built-in default (${DEFAULT_WARNING_DAYS} days)`;
  }
}
